import { isAnyCarsVehiclesCategory } from '@kijiji/category'
import { type SearchCategory, useGetCategoryPathQuery } from '@kijiji/generated/graphql-types'
import { ForSaleIcon, LocationOutlineIcon, SearchIcon } from '@kijiji/icons/src/icons'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { LowOrZeroResultsBanner } from '@/components/srp/low-or-zero-results/LowOrZeroResultsBanner'
import { SearchTip } from '@/components/srp/zero-results/SearchTip'
import {
  IconContainer,
  SearchTipsContainer,
  ZeroResultsPanel,
} from '@/components/srp/zero-results/styled'
import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { useGetSearchCategory } from '@/hooks/category/useGetSearchCategory'
import { useGetSearchKeyword } from '@/hooks/keywords/useGetSearchKeywords'
import { useSearchActions } from '@/hooks/srp/useSearchActions'
import { RemoteParamKeys, useToggle } from '@/lib/firebase/hooks'
import { trackEvent } from '@/lib/ga'
import { getSearchPageType } from '@/lib/ga/utils/getSearchPageType'
import { HeadlineText } from '@/ui/atoms/headline-text'
import ZeroSrpImage from '~/public/next-assets/images/zero-srp-image.png'

const ZeroResultsIcon = dynamic(() => import('@kijiji/icons/src/icons/ZeroResults'), {
  ssr: false,
})

export const ZeroResultsActionBox = () => {
  const { handleOpenModal } = useModal(MODAL_REGISTRY.SEARCH_LOCATION)

  const { t } = useTranslation(TRANSLATION_KEYS.SRP)
  const { colors } = useTheme()
  const { keyword } = useGetSearchKeyword()
  const { category } = useGetSearchCategory()
  const { refetchResults } = useSearchActions()

  const [parentCategory, setParentCategory] = useState<SearchCategory | null>(null)

  const gaPageType = getSearchPageType(keyword)

  const extendedSearchForLowOrZeroSrpToggle = useToggle(
    RemoteParamKeys.EXTENDED_SEARCH_FOR_NULL_AND_LOW_SRP
  )

  useGetCategoryPathQuery({
    fetchPolicy: 'cache-first',
    ssr: false,
    skip: !category.parentId,
    variables: { categoryId: category.parentId || ALL_CATEGORIES_ID_NUM },
    onCompleted: ({ category }) => {
      if (!category) {
        setParentCategory(null)
        return
      }

      const parentCategory: SearchCategory = {
        id: category.id,
        localizedName: category.localizedName ?? '',
        parentId: category.parentId,
      }
      setParentCategory(parentCategory)
    },
  })

  const handleCategoryChange = async () => {
    /** If there is no parent category, then there is no category to go up */
    if (!parentCategory) return

    trackEvent({ action: gaPageType, label: 'SuggestionBoxClick;cta="browse"' })
    await refetchResults({ categoryId: parentCategory.id })
  }

  const handleLocationChange = () => {
    trackEvent({ action: gaPageType, label: 'SuggestionBoxClick;cta="location"' })
    handleOpenModal()
  }

  const hasCategorySpecificLabels = isAnyCarsVehiclesCategory(category.id)

  if (extendedSearchForLowOrZeroSrpToggle?.enabled) {
    return (
      <LowOrZeroResultsBanner
        title={t(`srp:zero_results.title${hasCategorySpecificLabels ? '_per_category' : ''}`, {
          category: category.localizedName,
        })}
        description={t(
          `srp:zero_results.description${hasCategorySpecificLabels ? '_per_category' : ''}`
        )}
        image={ZeroSrpImage}
        imageHeight={79}
        imageWidth={100}
      />
    )
  }

  return (
    <ZeroResultsPanel $isFullWidth $alignItems="center" data-testid="zero-results-page">
      <>
        <IconContainer>
          <ZeroResultsIcon aria-hidden />
        </IconContainer>

        <HeadlineText size="large" as="h2" color={colors.grey.primary}>
          {t('srp:zero_results.title_old')}
        </HeadlineText>

        <SearchTipsContainer>
          {parentCategory && (
            <SearchTip
              icon={<ForSaleIcon aria-hidden />}
              text={t('srp:zero_results.broaden_category')}
              actionText={`${t('srp:zero_results.one_category_up')} ${parentCategory.localizedName}`}
              handleClick={handleCategoryChange}
            />
          )}

          <SearchTip
            icon={<SearchIcon aria-hidden />}
            text={t('srp:zero_results.try_different_keyword')}
          />

          <SearchTip
            icon={<LocationOutlineIcon aria-hidden />}
            text={t('srp:zero_results.expand_search_location')}
            actionText={t('srp:zero_results.edit_location')}
            handleClick={handleLocationChange}
          />
        </SearchTipsContainer>
      </>
    </ZeroResultsPanel>
  )
}
